import { useCallback, useEffect, useState } from "react";
import { usePortalMenu } from "../../../hooks/usePortalMenu";
import type { AlertConditionDropdownBinding } from "../../molecules/AlertConditionCard";

type ConditionDropdown = "metric" | "comparator";

export interface UseAlertConditionCardInternalState {
  metricDropdown: AlertConditionDropdownBinding;
  comparatorDropdown: AlertConditionDropdownBinding;
}

export const useAlertConditionCardInternalState =
  (): UseAlertConditionCardInternalState => {
    const [openDropdown, setOpenDropdown] =
      useState<ConditionDropdown | null>(null);
    const [metricQuery, setMetricQuery] = useState("");
    const [comparatorQuery, setComparatorQuery] = useState("");

    const closeDropdown = useCallback(() => setOpenDropdown(null), []);
    const toggleDropdown = useCallback((dropdown: ConditionDropdown) => {
      setOpenDropdown((current) =>
        current === dropdown ? null : dropdown,
      );
    }, []);

    useEffect(() => {
      if (openDropdown !== "metric") setMetricQuery("");
      if (openDropdown !== "comparator") setComparatorQuery("");
    }, [openDropdown]);

    const metricPortal = usePortalMenu<
      HTMLButtonElement,
      HTMLUListElement
    >({
      open: openDropdown === "metric",
      onOutsideClick: closeDropdown,
    });
    const comparatorPortal = usePortalMenu<
      HTMLButtonElement,
      HTMLUListElement
    >({
      open: openDropdown === "comparator",
      onOutsideClick: closeDropdown,
    });

    return {
      metricDropdown: {
        open: openDropdown === "metric",
        query: metricQuery,
        triggerRef: metricPortal.triggerRef,
        menuRef: metricPortal.menuRef,
        menuRect: metricPortal.rect,
        onToggle: () => toggleDropdown("metric"),
        onClose: closeDropdown,
        onQueryChange: setMetricQuery,
      },
      comparatorDropdown: {
        open: openDropdown === "comparator",
        query: comparatorQuery,
        triggerRef: comparatorPortal.triggerRef,
        menuRef: comparatorPortal.menuRef,
        menuRect: comparatorPortal.rect,
        onToggle: () => toggleDropdown("comparator"),
        onClose: closeDropdown,
        onQueryChange: setComparatorQuery,
      },
    };
  };
